import { lazy, Suspense, type ReactNode } from 'react';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '@/lib/queryClient';
import { AuthProvider } from './providers/AuthProvider';
import { ThemeProvider } from './providers/ThemeProvider';
import { NotificationProvider } from './providers/NotificationProvider';
import { NotificationToast } from '@/components/common/NotificationToast';
import { AppRoutes } from './routes';
import { AnalyticsIdentity } from './providers/AnalyticsIdentity';

// SP4.1 — When VITE_DEV_AUTH_BYPASS is not 'true' (e.g. production), Vite
// replaces the literal at build time, the ternary folds to null, and Rollup
// drops the DevAuthProvider chunk via dead-code elimination.
const isDevBypass = import.meta.env.VITE_DEV_AUTH_BYPASS === 'true';
const DevAuthProviderLazy = isDevBypass
  ? lazy(() =>
      import('./providers/DevAuthProvider').then((m) => ({ default: m.DevAuthProvider })),
    )
  : null;

function AuthShell({ children }: { children: ReactNode }) {
  if (DevAuthProviderLazy) {
    return (
      <Suspense fallback={null}>
        <DevAuthProviderLazy>{children}</DevAuthProviderLazy>
      </Suspense>
    );
  }
  return <AuthProvider>{children}</AuthProvider>;
}

export function App() {
  return (
    <BrowserRouter>
      <AuthShell>
        <AnalyticsIdentity />
        <QueryClientProvider client={queryClient}>
          <ThemeProvider>
            <NotificationProvider>
              <AppRoutes />
              <NotificationToast />
            </NotificationProvider>
          </ThemeProvider>
        </QueryClientProvider>
      </AuthShell>
    </BrowserRouter>
  );
}
